import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CalendarToday,
  Assignment,
  Schedule,
  CheckCircle,
  School,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const StudentExams = () => {
  const { user } = useAuth();
  const [examsData, setExamsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const BRAND = {
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    gradientAlt: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    surface: 'rgba(255, 255, 255, 0.95)',
    ink: '#0f172a',
    muted: '#64748b',
  };

  useEffect(() => {
    fetchExamsData();
  }, []);

  const fetchExamsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dashboard/student`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExamsData(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': '#16a34a',
      'completed': '#3b82f6',
      'scheduled': '#f59e0b',
    };
    return colors[status] || '#64748b';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: BRAND.gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Paper sx={{
          p: 4,
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <CircularProgress sx={{ color: '#f093fb', mb: 2 }} />
          <Typography variant="h6" sx={{ color: BRAND.ink }}>
            Loading exam schedule...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: BRAND.gradient,
      py: { xs: 3, md: 4 },
      px: { xs: 2, md: 4 },
    }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box sx={{ 
          mb: 4, 
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          p: { xs: 3, md: 4 }, 
          borderRadius: 4,
          boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.18)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: BRAND.gradient,
          }
        }}>
          <Typography variant="h3" fontWeight="800" sx={{ 
            background: BRAND.gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}>
            Exam Schedule
          </Typography>
          <Typography variant="h6" sx={{ color: BRAND.muted, fontWeight: 600 }}>
            View your upcoming and completed exams
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
            }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Assignment sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" fontWeight="800">
                  {examsData?.total_exams || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Exams
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(240, 147, 251, 0.4)',
            }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Schedule sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" fontWeight="800">
                  {examsData?.upcoming_exams?.length || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Upcoming
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(22, 163, 74, 0.4)',
            }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" fontWeight="800">
                  {examsData?.exams_attended || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Attended
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(245, 158, 11, 0.4)',
            }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <CalendarToday sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" fontWeight="800">
                  {examsData?.exams_this_month || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  This Month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Upcoming Exams */}
        <Paper sx={{
          p: 4,
          mb: 4,
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
          border: '1px solid rgba(255,255,255,0.18)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{
              width: 4,
              height: 32,
              background: BRAND.gradient,
              borderRadius: 2,
              mr: 2
            }} />
            <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
              Upcoming Exams
            </Typography>
          </Box>

          {examsData?.upcoming_exams && examsData.upcoming_exams.length > 0 ? (
            <Box>
              {examsData.upcoming_exams.map((exam, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    p: 3,
                    mb: 2,
                    bgcolor: '#f8fafc',
                    borderRadius: 3,
                    border: '2px solid #e2e8f0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: '#f1f5f9',
                      transform: 'translateX(4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: '#cbd5e1',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="700" sx={{ color: BRAND.ink, mb: 0.5 }}>
                        {exam.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Chip 
                          icon={<School />}
                          label={exam.class}
                          size="small"
                          sx={{ 
                            bgcolor: alpha('#667eea', 0.15),
                            color: '#667eea',
                            fontWeight: 600,
                          }}
                        />
                        <Chip 
                          label={exam.type}
                          size="small"
                          sx={{ 
                            bgcolor: alpha('#764ba2', 0.15),
                            color: '#764ba2',
                            fontWeight: 600,
                          }}
                        />
                        <Chip 
                          label={exam.status}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(getStatusColor(exam.status), 0.15),
                            color: getStatusColor(exam.status),
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        bgcolor: alpha('#f093fb', 0.1),
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                      }}>
                        <CalendarToday sx={{ fontSize: 20, color: '#f093fb' }} />
                        <Typography variant="body1" fontWeight="700" sx={{ color: BRAND.ink }}>
                          {formatDate(exam.exam_date)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid #e2e8f0',
                  }}>
                    <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                      Exam ID: {exam.id}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Schedule sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                No upcoming exams
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                You don't have any exams scheduled at the moment
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Recent Exam Results Preview */}
        <Paper sx={{
          p: 4,
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
          border: '1px solid rgba(255,255,255,0.18)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{
              width: 4,
              height: 32,
              background: BRAND.gradientAlt,
              borderRadius: 2,
              mr: 2
            }} />
            <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
              Recent Exam Results
            </Typography>
          </Box>

          {examsData?.recent_results && examsData.recent_results.length > 0 ? (
            <Box>
              {examsData.recent_results.slice(0, 5).map((result, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    p: 2.5,
                    mb: 1.5,
                    bgcolor: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#f1f5f9',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" sx={{ color: BRAND.ink }}>
                      {result.exam}
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND.muted }}>
                      {result.subject} • {result.exam_date}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {!result.is_absent && (
                      <Typography variant="body1" fontWeight="700" sx={{ color: BRAND.ink }}>
                        {result.marks_obtained}/{result.total_marks}
                      </Typography>
                    )}
                    <Chip 
                      label={result.is_absent ? 'Absent' : result.grade}
                      sx={{ 
                        bgcolor: result.is_absent ? '#fee2e2' : alpha('#667eea', 0.15),
                        color: result.is_absent ? '#dc2626' : '#667eea',
                        fontWeight: 700,
                        minWidth: 60
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Assignment sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                No results available yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                Your exam results will appear here once published
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default StudentExams;
